"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiWave } from "@/generated/models/ApiWave";
import { SLOW_MODE_MIN_MS, formatSlowModeCountdown, formatSlowModeInterval, getSlowModeRemainingMs } from "@/helpers/waves/slow-mode.helpers";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
interface SlowModeChatNoticeProps {
  readonly wave: ApiWave;
  readonly isDropMode: boolean;
}
export default function SlowModeChatNotice(t0) {
  const $ = _c(5);
  const {
    wave,
    isDropMode
  } = t0;
  const cooldownMs = wave.chat.slow_mode_cooldown_ms ?? null;
  const isSlowModeEnabled = typeof cooldownMs === "number" && cooldownMs >= SLOW_MODE_MIN_MS;
  const nextDropAllowed = wave.chat.next_drop_allowed ?? null;
  if (isDropMode || !isSlowModeEnabled) {
    return null;
  }
  const t1 = `${wave.id}:${cooldownMs}:${nextDropAllowed ?? "none"}`;
  let t2;
  if ($[0] !== cooldownMs || $[1] !== nextDropAllowed || $[2] !== t1 || $[3] !== wave.id) {
    t2 = <ActiveSlowModeChatNotice key={t1} waveId={wave.id} cooldownMs={cooldownMs} nextDropAllowed={nextDropAllowed} />;
    $[0] = cooldownMs;
    $[1] = nextDropAllowed;
    $[2] = t1;
    $[3] = wave.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
interface ActiveSlowModeChatNoticeProps {
  readonly waveId: string;
  readonly cooldownMs: number;
  readonly nextDropAllowed: number | null;
}
function ActiveSlowModeChatNotice(t0) {
  const $ = _c(13);
  const {
    waveId,
    cooldownMs,
    nextDropAllowed
  } = t0;
  const queryClient = useQueryClient();
  const [now, setNow] = useState(_temp);
  let t1;
  let t2;
  if ($[0] !== nextDropAllowed || $[1] !== queryClient || $[2] !== waveId) {
    t1 = () => {
      if (nextDropAllowed === null) {
        return;
      }
      if (nextDropAllowed <= Date.now()) {
        return;
      }
      let didInvalidate = false;
      const intervalId = setInterval(() => {
        const currentTime = Date.now();
        setNow(currentTime);
        if (currentTime < nextDropAllowed || didInvalidate) {
          return;
        }
        didInvalidate = true;
        clearInterval(intervalId);
        queryClient.invalidateQueries({
          queryKey: [QueryKey.WAVE, {
            wave_id: waveId
          }]
        }).catch(_temp2);
      }, 1000);
      return () => clearInterval(intervalId);
    };
    t2 = [nextDropAllowed, queryClient, waveId];
    $[0] = nextDropAllowed;
    $[1] = queryClient;
    $[2] = waveId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let remainingMs;
  let t3;
  if ($[5] !== cooldownMs || $[6] !== nextDropAllowed || $[7] !== now) {
    remainingMs = getSlowModeRemainingMs({
      nextDropAllowed,
      now
    });
    t3 = remainingMs > 0 ? `Slow mode: send again in ${formatSlowModeCountdown(remainingMs)}` : `Slow mode: one message every ${formatSlowModeInterval(cooldownMs)}`;
    $[5] = cooldownMs;
    $[6] = nextDropAllowed;
    $[7] = now;
    $[8] = remainingMs;
    $[9] = t3;
  } else {
    remainingMs = $[8];
    t3 = $[9];
  }
  const message = t3;
  const t4 = remainingMs > 0 ? "polite" : undefined;
  let t5;
  if ($[10] !== message || $[11] !== t4) {
    t5 = <p className="tw-mb-2 tw-mt-0 tw-text-[11px] tw-font-medium tw-leading-4 tw-text-iron-400" aria-live={t4}>{message}</p>;
    $[10] = message;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp2() {}
function _temp() {
  return Date.now();
}